<template>
  <v-container>
    <v-card
      max-width="800"
      color="#F8F8F8"
      class="pa-2 px-6 mx-auto report-card my-4"
    >
      <v-card-text class="d-flex align-center mb-2">
        <v-icon class="mr-2">mdi-magnify</v-icon>
        <span class="subtitle"> {{ $t('Filtros de Busca') }} </span>
      </v-card-text>

      <v-row class="ma-0" dense>
        <v-col cols="12" lg="6">
          <label class="label ml-1">{{ $t('Instituição') }}</label>
          <v-autocomplete
            :search-input.sync="institutionSearch"
            @blur="institutionSearch = ''"
            hide-details="auto"
            :rules="[(v) => v.length <= 10 || 'Máximo de 10 instituições']"
            v-model="filter.institutionIds"
            :items="institutions"
            multiple
            item-text="name"
            @change="getUnidades"
            item-value="id"
            :loading="institutionLoading"
            outlined
            class="primary-input"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" lg="6">
          <label class="label ml-1">{{ $t('Unidade') }}</label>
          <v-autocomplete
            hide-details="auto"
            v-model="filter.unidadeId"
            :items="unidades"
            item-value="id"
            item-text="name"
            :loading="unidadeLoading"
            placeholder="Todas"
            outlined
            class="primary-input"
          ></v-autocomplete>
        </v-col>

        <v-col
          cols="12"
          class="d-flex align-center justify-center mb-2 flex-wrap"
        >
          <v-menu
            ref="startMenu"
            :close-on-content-click="false"
            :return-value.sync="filter.startDate"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                depressed
                append-icon="mdi-calendar"
                class="primary-button"
                style="text-transform: none"
                readonly
                v-bind="attrs"
                v-on="on"
              >
                {{ `${formatMonth(filter.startDate)}` }}
              </v-btn>
            </template>
            <v-date-picker
              v-model="filter.startDate"
              type="month"
              locale="pt-br"
              no-title
              scrollable
              @change="$refs.startMenu.save(filter.startDate)"
            >
            </v-date-picker>
          </v-menu>

          <span class="mx-2 subtitle"> {{ $t('até') }} </span>

          <v-menu
            ref="endMenu"
            :close-on-content-click="false"
            :return-value.sync="filter.endDate"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                depressed
                append-icon="mdi-calendar"
                class="primary-button"
                style="text-transform: none"
                readonly
                v-bind="attrs"
                v-on="on"
              >
                {{ `${formatMonth(filter.endDate)}` }}
              </v-btn>
            </template>
            <v-date-picker
              v-model="filter.endDate"
              type="month"
              locale="pt-br"
              no-title
              scrollable
              @change="$refs.endMenu.save(filter.endDate)"
            >
            </v-date-picker>
          </v-menu>
        </v-col>

        <v-col
          class="d-flex align-center justify-center mb-4 flex-wrap"
          cols="12"
        >
          <v-btn depressed class="secondary-button mr-1" @click="applyFilter">
            <span>{{ $t('Filtrar') }}</span>
            <v-icon class="ml-2">mdi-magnify</v-icon>
          </v-btn>

          <v-btn depressed class="clear-button ml-1" @click="reset">
            <span>{{ $t('Limpar') }}</span>
            <v-icon class="ml-2">mdi-cached</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-row class="ma-0" :key="filterKey">
      <v-col cols="12" sm="6">
        <EquipmentBySectorChart
          :filterProp="filter"
          :institution="currentInstitution"
          :unidades="currentUnidades"
          :isResult="true"
          :title="indicatorTitle('equip_by_sector')"
          :api-endpoint-override="getIndicator('equip_by_sector').bigDataEndpoint"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <RequestBySector
          :filterProp="filter"
          :institution="currentInstitution"
          :unidades="currentUnidades"
          :isResult="true"
          :title="indicatorTitle('request_by_sector')"
          :api-endpoint-override="getIndicator('request_by_sector').bigDataEndpoint"
        />
      </v-col>
      <v-col cols="12">
        <SolicitedVConcluded
          :filterProp="filter"
          :institution="currentInstitution"
          :unidades="currentUnidades"
          :isResult="true"
          :title="indicatorTitle('solicited_v_concluded')"
          :api-endpoint-override="getIndicator('solicited_v_concluded').bigDataEndpoint"
        />
      </v-col>
      <v-col cols="12">
        <CorrectiveScheXExec
          :filterProp="filter"
          :institution="currentInstitution"
          :unidades="currentUnidades"
          :isResult="true"
          :title="indicatorTitle('corrective_sched_x_exec')"
          :api-endpoint-override="getIndicator('corrective_sched_x_exec').bigDataEndpoint"
        />
      </v-col>
      <v-col cols="12">
        <InternalResolvability
          :filterProp="filter"
          :institution="currentInstitution"
          :unidades="currentUnidades"
          :isResult="true"
          :title="indicatorTitle('internal_resolvability')"
          :api-endpoint-override="getIndicator('internal_resolvability').bigDataEndpoint"
        />
      </v-col>
      <v-col cols="12">
        <PreventiveXCorrective
          :filterProp="filter"
          :institution="currentInstitution"
          :unidades="currentUnidades"
          :isResult="true"
          :title="indicatorTitle('preventive_x_corrective')"
          :api-endpoint-override="getIndicator('preventive_x_corrective').bigDataEndpoint"
        />
      </v-col>
      <v-col cols="12">
        <PreventiveSchedXExec
          :filterProp="filter"
          :institution="currentInstitution"
          :unidades="currentUnidades"
          :isResult="true"
          :title="indicatorTitle('preventive_sched_x_exec')"
          :api-endpoint-override="getIndicator('preventive_sched_x_exec').bigDataEndpoint"
        />
      </v-col>
      <v-col cols="12">
        <CalibSchedXExec
          :filterProp="filter"
          :institution="currentInstitution"
          :unidades="currentUnidades"
          :isResult="true"
          :title="indicatorTitle('calib_sched_x_exec')"
          :api-endpoint-override="getIndicator('calib_sched_x_exec').bigDataEndpoint"
        />
      </v-col>
      <v-col cols="12">
        <EletricSecSchedXExec
          :filterProp="filter"
          :institution="currentInstitution"
          :unidades="currentUnidades"
          :isResult="true"
          :title="indicatorTitle('eletric_sec_sched_x_exec')"
          :api-endpoint-override="getIndicator('eletric_sec_sched_x_exec').bigDataEndpoint"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import ApiService from '../../../../../services/ApiService'
import EquipmentBySectorChart from '../../../Reports/components/GenerateReport/components/charts/quantitative/equipmentBySector.vue'
import RequestBySector from '../../../Reports/components/GenerateReport/components/charts/quantitative/requestBySector.vue'
import SolicitedVConcluded from '../../../Reports/components/GenerateReport/components/charts/quantitative/solicitedVConcluded.vue'
import CorrectiveScheXExec from '../../../Reports/components/GenerateReport/components/charts/quantitative/correctiveSchedXExec.vue'
import InternalResolvability from '../../../Reports/components/GenerateReport/components/charts/quantitative/internalResolvability.vue'
import PreventiveXCorrective from '../../../Reports/components/GenerateReport/components/charts/quantitative/preventiveXCorrective.vue'
import PreventiveSchedXExec from '../../../Reports/components/GenerateReport/components/charts/quantitative/preventiveSchedxExec.vue'
import CalibSchedXExec from '../../../Reports/components/GenerateReport/components/charts/quantitative/calibSchedXExec.vue'
import EletricSecSchedXExec from '../../../Reports/components/GenerateReport/components/charts/quantitative/eletricSecSchedXExec.vue'
import {
  getIndicatorById,
  getVisibleIndicators,
  getCategoryById,
} from '../../../Reports/indicators/indicatorRegistry'
import moment from 'moment'

export default {
  data: () => ({
    institutionSearch: '',
    filter: {
      page: 1,
      pageSize: 10,
      institutionIds: [],
      unidadeId: null,
    },
    filterKey: false,
    apiService: new ApiService(),
    unidades: [],
    institutions: [],
    unidadeLoading: false,
    institutionLoading: false,
  }),

  created() {
    this.institutionId = JSON.parse(localStorage.getItem('institution')).id
    this.institution = JSON.parse(localStorage.getItem('institution'))
    this.filter.institutionIds = [this.institutionId]
    this.getInstitutions()
  },

  computed: {
    currentInstitution() {
      return this.institution || { id: this.institutionId }
    },
    currentUnidades() {
      return this.filter.unidadeId ? [this.filter.unidadeId] : []
    },
  },

  components: {
    EquipmentBySectorChart,
    RequestBySector,
    SolicitedVConcluded,
    CorrectiveScheXExec,
    InternalResolvability,
    PreventiveXCorrective,
    PreventiveSchedXExec,
    CalibSchedXExec,
    EletricSecSchedXExec,
  },

  methods: {
    getIndicator(id) {
      return getIndicatorById(id) || {}
    },

    indicatorTitle(id) {
      const ind = getIndicatorById(id)
      if (!ind) return ''
      const cat = getCategoryById(ind.categoryId)
      const visible = getVisibleIndicators(ind.categoryId, this.institution)
      const found = visible.find((v) => v.id === id)
      if (!found || !cat) return ind.nameKey
      return `${cat.prefix}.${found.displayNumber} - ${this.$t(ind.nameKey)}`
    },

    applyFilter() {
      if (this.filter.institutionIds.length > 10) {
        this.$toast.warning('Máximo de 10 instituições por filtro!')
        return
      }

      this.filterKey = !this.filterKey
    },

    reset() {
      this.filter = {
        page: 1,
        pageSize: 10,
        institutionIds: [],
        unidadeId: null,
      }
      this.applyFilter()
    },

    formatMonth(date) {
      return moment(date).locale(this.$i18n.locale).format('MMMM/YYYY')
    },

    async getInstitutions() {
      this.institutionLoading = true

      await this.apiService
        .get('institution/list')
        .then((response) => {
          this.institutions = response.content
          this.institutionLoading = false
        })
        .catch((err) => {})
    },

    async getUnidades() {
      this.unidadeLoading = true

      await this.apiService
        .post(`unidade/listByIntitutionIds`, {
          institutionIds: this.filter.institutionIds,
        })
        .then((response) => {
          this.unidades = response.content
          this.unidadeLoading = false
        })
        .catch((err) => {
          this.unidadeLoading = false
        })
    },
  },
}
</script>
